<script lang="ts">
  import { onMount } from "svelte"
  import { fade, fly } from "svelte/transition"
  import ThreeAIBackground from "$lib/components/ThreeAIBackground.svelte"

  let loaded = false

  onMount(() => {
    loaded = true
    // Force dark theme
    document.documentElement.style.backgroundColor = "#0a1122"
    document.body.style.backgroundColor = "#0a1122"
  })
</script>

<svelte:head>
  <title>Hollas.net - Digital Transformation & AI Solutions</title>
  <meta
    name="description"
    content="Hollas AB specializes in digital transformation, AI implementation, and innovative technology solutions for businesses looking to grow and evolve."
  />
</svelte:head>

<!-- Full-screen AI background animation -->
<ThreeAIBackground />

<!-- Main content -->
<div
  class="relative isolate overflow-hidden min-h-screen"
  in:fade={{ duration: 500, delay: 200 }}
>
  <!-- Hero section with translucent background -->
  <div class="mx-auto max-w-7xl px-6 pb-24 pt-10 sm:pb-32 lg:px-8 lg:py-40">
    <div class="hero-content-box">
      <div class="mx-auto max-w-2xl lg:mx-0 lg:max-w-xl lg:pt-8">
        <div in:fly={{ y: 50, duration: 600, delay: 200 }}>
          <div class="mt-6 sm:mt-10 lg:mt-8">
            <a href="/contact" class="inline-flex space-x-6">
              <span
                class="rounded-full bg-sky-500/30 px-3 py-1 text-sm font-semibold leading-6 text-sky-100 ring-1 ring-inset ring-sky-500/30"
              >
                What's new
              </span>
              <span
                class="inline-flex items-center space-x-2 text-sm font-medium leading-6 text-gray-200"
              >
                <span>Just launched our new AI consulting services</span>
                <svg
                  class="h-5 w-5 text-gray-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    fill-rule="evenodd"
                    d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
                    clip-rule="evenodd"
                  />
                </svg>
              </span>
            </a>
          </div>
          <h1
            class="mt-8 text-4xl font-bold tracking-tight text-white sm:text-6xl text-shadow"
          >
            Digital transformation for the AI era
          </h1>
          <p class="mt-6 text-lg leading-8 text-gray-200">
            We help businesses embrace AI-powered solutions that unlock new
            possibilities, automate workflows, and drive growth. Discover how
            our expertise can transform your operations.
          </p>
          <div class="mt-10 flex items-center gap-x-6">
            <a
              href="/contact"
              class="rounded-md bg-sky-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400"
            >
              Get started
            </a>
            <a href="/about" class="text-sm font-semibold leading-6 text-white">
              Learn more <span aria-hidden="true">→</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- Features Section -->
<div class="bg-slate-800/70 backdrop-blur-md py-24 sm:py-32">
  <div class="mx-auto max-w-7xl px-6 lg:px-8">
    <div class="mx-auto max-w-2xl lg:text-center">
      <h2 class="text-base font-semibold leading-7 text-sky-400">
        Deploy Faster
      </h2>
      <p class="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
        Everything you need to deploy your AI solutions
      </p>
      <p class="mt-6 text-lg leading-8 text-gray-300">
        From strategy development to implementation and maintenance, we provide
        end-to-end AI solutions that integrate seamlessly with your existing
        systems.
      </p>
    </div>
    <div class="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
      <dl
        class="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3"
      >
        <div class="flex flex-col">
          <dt
            class="flex items-center gap-x-3 text-base font-semibold leading-7 text-white"
          >
            <svg
              class="h-5 w-5 flex-none text-sky-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M5.5 17a4.5 4.5 0 01-1.44-8.765 4.5 4.5 0 018.302-3.046 3.5 3.5 0 014.504 4.272A4 4 0 0115 17H5.5zm3.75-2.75a.75.75 0 001.5 0V9.66l1.95 2.1a.75.75 0 101.1-1.02l-3.25-3.5a.75.75 0 00-1.1 0l-3.25 3.5a.75.75 0 101.1 1.02l1.95-2.1v4.59z"
                clip-rule="evenodd"
              />
            </svg>
            Custom AI Models
          </dt>
          <dd
            class="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-300"
          >
            <p class="flex-auto">
              Tailored AI solutions designed specifically for your business
              needs, trained on your data for maximum relevance and accuracy.
            </p>
            <p class="mt-6">
              <a
                href="/services"
                class="text-sm font-semibold leading-6 text-sky-400"
                >Learn more <span aria-hidden="true">→</span></a
              >
            </p>
          </dd>
        </div>
        <div class="flex flex-col">
          <dt
            class="flex items-center gap-x-3 text-base font-semibold leading-7 text-white"
          >
            <svg
              class="h-5 w-5 flex-none text-sky-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M10 1a4.5 4.5 0 00-4.5 4.5V9H5a2 2 0 00-2 2v6a2 2 0 002 2h10a2 2 0 002-2v-6a2 2 0 00-2-2h-.5V5.5A4.5 4.5 0 0010 1zm3 8V5.5a3 3 0 10-6 0V9h6z"
                clip-rule="evenodd"
              />
            </svg>
            Enterprise Integration
          </dt>
          <dd
            class="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-300"
          >
            <p class="flex-auto">
              Seamlessly integrate AI capabilities into your existing business
              systems with robust security and scalability.
            </p>
            <p class="mt-6">
              <a
                href="/services"
                class="text-sm font-semibold leading-6 text-sky-400"
                >Learn more <span aria-hidden="true">→</span></a
              >
            </p>
          </dd>
        </div>
        <div class="flex flex-col">
          <dt
            class="flex items-center gap-x-3 text-base font-semibold leading-7 text-white"
          >
            <svg
              class="h-5 w-5 flex-none text-sky-400"
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M15.312 11.424a5.5 5.5 0 01-9.201 2.466l-.312-.311h2.433a.75.75 0 000-1.5H3.989a.75.75 0 00-.75.75v4.242a.75.75 0 001.5 0v-2.43l.31.31a7 7 0 0011.712-3.138.75.75 0 00-1.449-.39zm1.23-3.723a.75.75 0 00.219-.53V2.929a.75.75 0 00-1.5 0V5.36l-.31-.31A7 7 0 003.239 8.188a.75.75 0 101.448.389A5.5 5.5 0 0113.89 6.11l.311.31h-2.432a.75.75 0 000 1.5h4.243a.75.75 0 00.53-.219z"
                clip-rule="evenodd"
              />
            </svg>
            Continuous Optimization
          </dt>
          <dd
            class="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-300"
          >
            <p class="flex-auto">
              Ongoing monitoring, maintenance, and updates to ensure your AI
              systems continue to deliver optimal performance as your business
              evolves.
            </p>
            <p class="mt-6">
              <a
                href="/services"
                class="text-sm font-semibold leading-6 text-sky-400"
                >Learn more <span aria-hidden="true">→</span></a
              >
            </p>
          </dd>
        </div>
      </dl>
    </div>
  </div>
</div>

<!-- Services Overview -->
<div class="bg-slate-900/80 backdrop-blur-md py-24 sm:py-32">
  <div class="mx-auto max-w-7xl px-6 lg:px-8">
    <div class="mx-auto max-w-2xl lg:text-center">
      <h2 class="text-base font-semibold leading-7 text-sky-400">
        Our Services
      </h2>
      <p class="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">
        AI-powered solutions for every industry
      </p>
      <p class="mt-6 text-lg leading-8 text-gray-300">
        We provide comprehensive AI services tailored to your specific industry
        and business needs.
      </p>
    </div>

    <div
      class="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 sm:mt-20 lg:max-w-none lg:grid-cols-3"
    >
      <!-- Service 1 -->
      <div class="flex flex-col overflow-hidden rounded-lg bg-slate-800">
        <div class="flex flex-1 flex-col justify-between p-6">
          <div class="flex-1">
            <div class="flex items-center gap-x-4 text-xs">
              <span
                class="inline-flex items-center rounded-md bg-sky-400/10 px-2 py-1 text-xs font-medium text-sky-400"
                >Strategy</span
              >
            </div>
            <div class="mt-4 block">
              <h3 class="text-xl font-semibold leading-6 text-white">
                AI Strategy & Consulting
              </h3>
              <p class="mt-3 text-base text-gray-300">
                Develop a comprehensive AI roadmap for your business with our
                expert consultants.
              </p>
            </div>
          </div>
          <div class="mt-6">
            <a
              href="/services/strategy"
              class="text-sm font-semibold leading-6 text-sky-400"
              >Learn more <span aria-hidden="true">→</span></a
            >
          </div>
        </div>
      </div>

      <!-- Service 2 -->
      <div class="flex flex-col overflow-hidden rounded-lg bg-slate-800">
        <div class="flex flex-1 flex-col justify-between p-6">
          <div class="flex-1">
            <div class="flex items-center gap-x-4 text-xs">
              <span
                class="inline-flex items-center rounded-md bg-sky-400/10 px-2 py-1 text-xs font-medium text-sky-400"
                >Development</span
              >
            </div>
            <div class="mt-4 block">
              <h3 class="text-xl font-semibold leading-6 text-white">
                Custom AI Solutions
              </h3>
              <p class="mt-3 text-base text-gray-300">
                Development of bespoke AI applications designed to meet your
                specific business requirements.
              </p>
            </div>
          </div>
          <div class="mt-6">
            <a
              href="/services/development"
              class="text-sm font-semibold leading-6 text-sky-400"
              >Learn more <span aria-hidden="true">→</span></a
            >
          </div>
        </div>
      </div>

      <!-- Service 3 -->
      <div class="flex flex-col overflow-hidden rounded-lg bg-slate-800">
        <div class="flex flex-1 flex-col justify-between p-6">
          <div class="flex-1">
            <div class="flex items-center gap-x-4 text-xs">
              <span
                class="inline-flex items-center rounded-md bg-sky-400/10 px-2 py-1 text-xs font-medium text-sky-400"
                >Support</span
              >
            </div>
            <div class="mt-4 block">
              <h3 class="text-xl font-semibold leading-6 text-white">
                AI Implementation & Support
              </h3>
              <p class="mt-3 text-base text-gray-300">
                End-to-end implementation services and ongoing support to ensure
                your AI solutions deliver continuous value.
              </p>
            </div>
          </div>
          <div class="mt-6">
            <a
              href="/services/support"
              class="text-sm font-semibold leading-6 text-sky-400"
              >Learn more <span aria-hidden="true">→</span></a
            >
          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- CTA Section -->
<div class="bg-slate-900">
  <div class="px-6 py-24 sm:px-6 sm:py-32 lg:px-8">
    <div class="mx-auto max-w-2xl text-center">
      <h2 class="text-3xl font-bold tracking-tight text-white sm:text-4xl">
        Ready to transform your business?
      </h2>
      <p class="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-300">
        Start your AI journey today and stay ahead of the competition with our
        cutting-edge solutions.
      </p>
      <div class="mt-10 flex items-center justify-center gap-x-6">
        <a
          href="/contact"
          class="rounded-md bg-sky-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sky-400"
        >
          Get started
        </a>
        <a href="/about" class="text-sm font-semibold leading-6 text-white"
          >Learn more <span aria-hidden="true">→</span></a
        >
      </div>
    </div>
  </div>
</div>

<style>
  /* Add text shadow for better contrast with the animated background */
  .text-shadow {
    text-shadow: 0px 2px 4px rgba(0, 0, 0, 0.8);
  }

  /* Hero content box with translucent background */
  .hero-content-box {
    position: relative;
    background: rgba(
      15,
      25,
      45,
      0.65
    ); /* Slightly lighter blue with white hint */
    backdrop-filter: blur(4px); /* Less blur effect */
    -webkit-backdrop-filter: blur(4px); /* For Safari */
    border: 1px solid rgba(255, 255, 255, 0.1); /* Subtle white border */
    border-radius: 16px;
    padding: 2.5rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  }

  /* Removed hover effect entirely */

  /* Frosted glass effect for other sections */
  :global(.backdrop-blur-md) {
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }

  /* Ensure content is positioned above the background */
  :global(body) {
    position: relative;
    z-index: 1;
  }
</style>
